import React, { useState, useEffect, useCallback } from 'react';
import { Table, Button, Spinner, Alert, Form, InputGroup, Pagination, Badge } from 'react-bootstrap';
import * as adminApi from '../../Services/adminApi';
import { useDebounce } from 'use-debounce'; 

const ManageUsers = () => {
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    // Pagination state
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);

    // Filter and Search state
    const [searchTerm, setSearchTerm] = useState('');
    const [debouncedSearchTerm] = useDebounce(searchTerm, 500);
    const [filters, setFilters] = useState({ role: 'all', status: 'all' });

    const fetchUsers = useCallback(async () => {
        setLoading(true);
        try {
            const params = {
                page: currentPage,
                limit: 10,
                searchTerm: debouncedSearchTerm,
                ...filters,
            };
            // The 'usersData' variable now holds the direct response object
            const usersData = await adminApi.getUsers(params);

            // --- FIX IS HERE ---
            // Remove the .data from the response
            setUsers(usersData.users);
            setTotalPages(usersData.totalPages);
            // --- END OF FIX ---

            setError('');
        } catch (err) {
            setError('Failed to fetch users.');
            console.error(err);
        } finally {
            setLoading(false);
        }
    }, [currentPage, debouncedSearchTerm, filters]);

    useEffect(() => {
        fetchUsers();
    }, [fetchUsers]);

    const handleFilterChange = (e) => {
        const { name, value } = e.target;
        setFilters(prev => ({ ...prev, [name]: value }));
        setCurrentPage(1); // Reset to first page on filter change
    };

    const handleBanToggle = async (user) => {
        if (window.confirm(`Are you sure you want to ${user.is_banned ? 'unban' : 'ban'} ${user.username}?`)) {
            try {
                await adminApi.updateUser(user.id, { is_banned: !user.is_banned });
                fetchUsers(); // Re-fetch to show updated status
            } catch (err) {
                alert('Failed to update user status.');
            }
        }
    };

    const handleDeleteUser = async (userId, username) => {
        if (window.confirm(`Are you sure you want to PERMANENTLY DELETE ${username}? This cannot be undone.`)) {
            try {
                await adminApi.deleteUser(userId);
                fetchUsers(); // Re-fetch to remove user from list
            } catch (err) {
                alert('Failed to delete user.');
            }
        }
    }

    return (
        <div>
            <h2>User Management</h2>
            <hr />

            {/* Filters and Search */}
            <InputGroup className="mb-3">
                 <InputGroup.Text><i className="fas fa-search"></i></InputGroup.Text>
                <Form.Control
                    placeholder="Search by name, username, or email..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                />
                <Form.Select name="role" onChange={handleFilterChange} style={{ maxWidth: '150px' }}>
                    <option value="all">All Roles</option>
                    <option value="user">User</option>
                    <option value="admin">Admin</option>
                </Form.Select>
                <Form.Select name="status" onChange={handleFilterChange} style={{ maxWidth: '150px' }}>
                    <option value="all">All Statuses</option>
                    <option value="active">Active</option>
                    <option value="banned">Banned</option>
                    <option value="unconfirmed">Unconfirmed</option>
                </Form.Select>
            </InputGroup>

            {loading && <div className="text-center"><Spinner animation="border" /></div>}
            {error && <Alert variant="danger">{error}</Alert>}

            {!loading && !error && (
                <>
                    <Table striped bordered hover responsive>
                        <thead>
                            <tr>
                                <th>ID</th>
                                <th>Username</th>
                                <th>Email</th>
                                <th>Role</th>
                                <th>Status</th>
                                <th>Joined</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {users.map(user => (
                                <tr key={user.id}>
                                    <td>{user.id}</td>
                                    <td>{user.username}</td>
                                    <td>{user.email}</td>
                                    <td>
                                        <Badge bg={user.role === 'admin' ? 'primary' : 'secondary'}>
                                            {user.role}
                                        </Badge>
                                    </td>
                                    <td>
                                        {user.is_banned ? <Badge bg="danger">Banned</Badge> :
                                            !user.email_confirmed ? <Badge bg="warning" text="dark">Unconfirmed</Badge> :
                                                <Badge bg="success">Active</Badge>}
                                    </td>
                                    <td>{new Date(user.created_at).toLocaleDateString()}</td>
                                    <td>
                                        <Button variant={user.is_banned ? "outline-success" : "outline-warning"} size="sm" className="me-2" onClick={() => handleBanToggle(user)}>
                                            {user.is_banned ? 'Unban' : 'Ban'}
                                        </Button>
                                        <Button variant="outline-danger" size="sm" onClick={() => handleDeleteUser(user.id, user.username)}>Delete</Button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </Table>

                    {totalPages > 1 && (
                        <Pagination className="justify-content-center">
                            <Pagination.Prev onClick={() => setCurrentPage(p => Math.max(p - 1, 1))} disabled={currentPage === 1} />
                            <Pagination.Item active>{currentPage} of {totalPages}</Pagination.Item>
                            <Pagination.Next onClick={() => setCurrentPage(p => Math.min(p + 1, totalPages))} disabled={currentPage === totalPages} />
                        </Pagination>
                    )}
                </>
            )}
        </div>
    );
};

export default ManageUsers;